import {DocMgmtService} from '../../../services/doc-mgmt.service';
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {TableConfig} from '../../shared/table/table-config/table-config';
import {EMPTY, Subject, switchMap, takeUntil} from 'rxjs';
import {TableComponent} from '../../shared/table/table.component';
import {RightPanelService} from '../../../services/right-panel.service';
import {Link} from 'src/app/models/link.model';
import {HandleErrorService} from 'src/app/services/handle-error.service';
import {CustomTranslateService} from 'src/app/services/custom-translate.service';
import {TerminalService} from '../../../services/terminal.service';
import {ConfigurationGroup} from '../../../models/configuration-group.model';
import {Terminal} from 'src/app/models/terminal.model';
import * as cloneDeep from 'lodash/cloneDeep';
import {CommonConfig} from '../../shared/config/common-config';
import {FileMetadata} from 'src/app/models/file-metadata.model';
import {ReloadDataService} from 'src/app/services/reload-data.service';
import {AdminLoginService} from '../../../services/admin-login.service';
import {InfiniteScrollDirective} from 'ngx-infinite-scroll';
import Utils from '../../../utils/utils';
import {RowData} from '../../../models/rowdata.model';
import {catchError} from 'rxjs/operators';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {ExtendValues, ParameterSortKey, ParameterSortOrder, PropertyDataService} from '../../../services/property-data.service';
import {PropertyDataTransferService} from 'src/app/services/property-data-transfer.service';
import {FileKeyProperties} from '../../../models/fileKeyProperties.model';
import {AssignmentDialogComponent} from '../../shared/assignment-dialog/assignment-dialog.component';
import { MonitoringService } from '../../../utils/monitoring.service';


@Component({
  selector: 'app-dms-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss']
})

export class FileComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filterValue: string;
  @Input() selectedColumns: any[];

  @ViewChild('fileTable') private readonly fileTable: TableComponent;
  @ViewChild(InfiniteScrollDirective) private readonly infiniteScrollDirective: InfiniteScrollDirective;

  tableConfig: TableConfig;
  tableData: FileMetadata[] = [];
  terminals: Terminal[];
  configGroups: ConfigurationGroup[];
  commonConfig: CommonConfig;
  isWebView: boolean;
  private readonly PAGE_SIZE = 20;
  private readonly FIRST_PAGE = 0;
  private readonly COLUMN_OPTIONS_SORT_PROPERTY = ParameterSortKey.ID;
  private readonly COLUMN_OPTIONS_SORT_ORDER = ParameterSortOrder.ASC;

  @Input() startDateFilter: Date;
  @Input() endDateFilter: Date;
  @Input() pageNumber: number;
  isDeleteInProgress: boolean;
  isAssignmentOperation: boolean;
  private readonly stop$: Subject<void> = new Subject<void>();
  @Input() selectionChange!: () => void;

  constructor(
    private readonly docMgmtService: DocMgmtService,
    private readonly rightPanelService: RightPanelService,
    private readonly handleErrorService: HandleErrorService,
    private readonly translateService: CustomTranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly terminalService: TerminalService,
    private readonly reloadDataService: ReloadDataService,
    private readonly adminLoginService: AdminLoginService,
    private readonly propertyDataService: PropertyDataService,
    private readonly propertyDataTransferService: PropertyDataTransferService,
    private monitoringService: MonitoringService
  ) {
    this.updateWebViewOnScreenSizeChange();
  }

  ngOnInit(): void {

    this.handleCustomerSelectionChange();
    this.createCommonConfig();
    this.loadFileMetadataIfReloaded();
    this.buildTableConfig();
    this.subscribeToObservables();
    this.handleAssignment();

    AssignmentDialogComponent.assignmentDoneEvent.subscribe((fileMetadata: FileMetadata) => {
      this.isAssignmentOperation = true;
      this.docMgmtService.fileMetadata.next([fileMetadata]);
    });

    this.rightPanelService.filterEvent.subscribe((event) => {
      if (event.isFilterApplied || !event.reset) {
        this.commonConfig.pageNumber = 0;
      }
    });
  }

  private handleCustomerSelectionChange(): void {
    this.adminLoginService.customerSelectionChanged$
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        this.resetTable();
      });
  }

  private createCommonConfig(): void {
    if (!this.commonConfig) {
      this.commonConfig = new CommonConfig();
    }
  }

  private loadFileMetadataIfReloaded(): void {
    if (this.reloadDataService.getShouldReload()) {
      this.docMgmtService.getFileMetadata();
    }
  }

  private buildTableConfig(): void {
    this.tableConfig = new TableConfig();
    this.tableConfig.displayedColumnNames = this.selectedColumns;
    this.tableConfig.iconColumnName = 'fileName';
    this.tableConfig.showCheckbox = false;
    this.tableConfig.rowClickAction = true;
    this.tableConfig.infiniteScrollEnabled = true;
    this.tableConfig.pagination = false;
    this.tableConfig.showSearchBar = false;

    const downloadAction = Utils.buildDownloadAction(this.translateService);
    const previewAction = Utils.buildPreviewAction(this.translateService);
    const deleteAction = Utils.buildDeleteAction(this.translateService);
    const assignPropertyAction = Utils.buildAssignPropertyAction(this.translateService);

    this.tableConfig.actionMenuList = [
      deleteAction,
      downloadAction,
      previewAction,
      assignPropertyAction
    ];
  }

  private updateWebViewOnScreenSizeChange(): void {
    this.breakpointObserver.observe(Breakpoints.XSmall)
      .pipe(takeUntil(this.stop$))
      .subscribe((result) => {
        this.isWebView = !result.matches;
      });
  }

  onSelectionChange(selectedColumns): void {
    this.selectedColumns = selectedColumns;
    this.tableConfig.displayedColumnNames = this.selectedColumns;
    sessionStorage.setItem('selectedColumns', JSON.stringify(this.selectedColumns));
  }

  private subscribeToObservables(): void {
    this.subscribeToFileConfigPageNumber();
    this.subscribeToFileMetadata();
    this.subscribeToTerminals();
    this.subscribeToConfigurationGroups();
  }

  private subscribeToFileConfigPageNumber(): void {
    this.docMgmtService.fileConfigPageNumber.pipe(takeUntil(this.stop$)).subscribe((value) => {
      if (value) {
        this.commonConfig = new CommonConfig();
        this.commonConfig.searchFilterValue = this.filterValue;
      }
    });
  }

  private subscribeToFileMetadata(): void {
    this.docMgmtService.fileMetadata.pipe(takeUntil(this.stop$)).subscribe((fileData) => {
      this.processFileData(fileData);
    });
  }

  private subscribeToTerminals(): void {
    this.terminalService.terminals.pipe(takeUntil(this.stop$)).subscribe((value) => {
      if (value) {
        this.terminals = value;
      }
    });
  }

  private subscribeToConfigurationGroups(): void {
    this.terminalService.configurationGroups.pipe(takeUntil(this.stop$)).subscribe((value) => {
      if (value) {
        this.configGroups = value;
      }
    });
  }

  private resetTable(): void {
    this.setTableData(null);
    this.docMgmtService.changePageValue();
    this.infiniteScrollDirective.destroyScroller();
    this.infiniteScrollDirective.setup();
  }

  private processFileData(fileData: FileMetadata[]): void {
    if (fileData !== null) {
      fileData = fileData.map((file: FileMetadata) => {
        file.canPreview = Utils.isPreviewable(file.fileType.toLowerCase());
        return file;
      });
      if (!this.getTableData()) {
        this.setTableData(fileData);
      } else if (this.commonConfig.pageNumber === 0 || this.isDeleteInProgress) {
        if (this.isSingleFileMetadata(fileData) && this.isAssignmentOperation) {
          this.updateTableDataOnFileMetadataUpdate(fileData[0]);
        } else {
          this.setTableData(fileData);
        }
      } else {
        if (this.isSingleFileMetadata(fileData) && this.isAssignmentOperation) {
          this.updateTableDataOnFileMetadataUpdate(fileData[0]);
        } else {
          fileData.forEach((row) => {
            this.setTableData([...this.getTableData(), row]);
          });
          this.setTableData(cloneDeep(this.getTableData()));
        }
      }
    }
  }

  // This is the case when the assignment of properties is done
  // It sends a single FileMetadata on the FileMetadata array
  private isSingleFileMetadata(fileData: FileMetadata[]): boolean {
    return fileData.length === 1;
  }

  private updateTableDataOnFileMetadataUpdate(fileData: FileMetadata): void {
    this.tableData = this.tableData.length === 0 ? [fileData] : this.tableData.map(existingFile => {
      if (existingFile.fileKey === fileData.fileKey) {
        return {...existingFile, ...fileData};
      }
      return existingFile;
    });
    this.isAssignmentOperation = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildTableConfig();
    this.createCommonConfig();
    this.setConfigurationOnChange();
    if (this.hasFirstFilterValueChanged(changes)) {
      this.setCommonConfigConfiguration(changes);
    }
  }

  private setConfigurationOnChange(): void {
    this.commonConfig.startDate = this.startDateFilter;
    this.commonConfig.endDate = this.endDateFilter;
    this.commonConfig.pageNumber = this.pageNumber ? this.pageNumber : 0;
  }

  private hasFirstFilterValueChanged(changes: SimpleChanges): boolean {
    return changes.filterValue && !changes.filterValue?.firstChange;
  }

  private setCommonConfigConfiguration(changes: SimpleChanges): void {
    this.commonConfig.pageNumber = 0;
    this.commonConfig.searchFilterValue = changes.filterValue.currentValue;
    this.docMgmtService.getFileMetadata({
      filterValue: this.commonConfig.searchFilterValue,
      startDate: this.commonConfig.startDate?.getTime(),
      endDate: this.commonConfig.endDate?.getTime(),
    });
  }

  ngOnDestroy() {
    this.clearComponentSessionStorage();
    this.stopSubscriptions();
  }

  private clearComponentSessionStorage(): void {
    sessionStorage.removeItem('selectedColumns');
  }


  private stopSubscriptions(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  rowSelected(row) {
    if (row) {
      this.rightPanelService.selectedFile.next(row);
    }
  }

  clearTableSelection() {
    if (this.fileTable) {
      this.fileTable.clearSelection();
    }
  }

  protected gridAction(data: { type: string; row: RowData }): void {
    switch (data?.type) {
      case Utils.ACTION_DOWNLOAD:
        this.monitoringService.sendMonitoringLogs('FILES_FILE_DOWNLOAD');
        Utils.handleDownload$(
          this.docMgmtService.downloadFile$(data.row.fileKey),
          data.row.fileName
        ).pipe(
          takeUntil(this.stop$),
          catchError(() => {
            this.handleErrorService.displayErrorDialog(
              this.translateService.getTranslatedValue('downloadUnavailable'),
              ''
            );
            return EMPTY;
          })
        ).subscribe(() => this.fileTable.onActionComplete());
        break;
      case Utils.ACTION_PREVIEW:
        this.monitoringService.sendMonitoringLogs('FILES_FILE_PREVIEW');
        Utils.handlePreview$(
          this.docMgmtService.downloadFile$(data.row.fileKey),
          data.row.fileName
        ).pipe(
          takeUntil(this.stop$),
          catchError(() => {
            this.handleErrorService.displayErrorDialog(
              this.translateService.getTranslatedValue('previewUnavailable'),
              ''
            );
            return EMPTY;
          })
        ).subscribe(() => this.fileTable.onActionComplete());
        break;
      case Utils.ACTION_DELETE:
        this.monitoringService.sendMonitoringLogs('FILES_FILE_DELETE');
        this.handleDeleteFile(data.row.fileKey);
        break;
    }
  }

  private handleAssignment(): void {
    this.propertyDataService.getValuesForAllKeys({
      extend: [ExtendValues.VALUES],
      page: this.FIRST_PAGE,
      size: this.PAGE_SIZE,
      sort: `${this.COLUMN_OPTIONS_SORT_PROPERTY},${this.COLUMN_OPTIONS_SORT_ORDER}`,
  }).pipe(
      takeUntil(this.stop$)
    ).subscribe(
      (responses: FileKeyProperties[]) => {
        this.propertyDataTransferService.setPropertyValuesList(responses);
      },
      error => {
        this.handleErrorService.displayErrorDialog(
          this.translateService.getTranslatedValue('valuesNotRetrieved'),
          ''
        );
        this.fileTable.onActionComplete();
      });
  }

  private handleDeleteFile(fileKey: string): void {
    this.docMgmtService
      .getAssignDetails(fileKey, null, null)
      .pipe(
        takeUntil(this.stop$),
        switchMap((links: Link[]) => {
          if (links.length === 0) {
            return this.docMgmtService.deleteFile$(fileKey);
          } else {
            this.handleErrorService.displayErrorDialog(
              this.translateService.getTranslatedValue('deleteError'),
              ''
            );
            return EMPTY;
          }
        }),
        catchError(() => {
          this.handleErrorService.displayErrorDialog(
            this.translateService.getTranslatedValue('deleteUnavailable'),
            ''
          );
          return EMPTY;
        })
      )
      .subscribe({
        next: (response) => {
          if (response !== undefined) {
            this.updateFileDataAfterDelete(fileKey);
          } else {
            this.handleDeleteError();
          }
        },
        error: () => this.handleDeleteError()
      });
  }

  private handleDeleteError(): void {
    this.handleErrorService.displayErrorDialog(
      this.translateService.getTranslatedValue('deleteErrorServer'),
      ''
    );
    this.setIsDeleteInProgress(false);
  }

  private updateFileDataAfterDelete(fileKey: string): void {
    this.setIsDeleteInProgress(true);
    const existingFileData = this.getTableData();
    this.closePanelAndClearSelection(fileKey);
    const updatedFileData = this.removeFileByFileKey(existingFileData, fileKey);
    this.docMgmtService.fileMetadata.next(updatedFileData);
    this.updateFullFileMetadata(fileKey);
    this.setIsDeleteInProgress(false);
  }

  private closePanelAndClearSelection(fileKey: string): void {
    if (this.rightPanelService.selectedFile.getValue()?.fileKey === fileKey) {
      this.rightPanelService.closePanel();
      this.clearTableSelection();
    }
  }

  private removeFileByFileKey(files: FileMetadata[], fileKey: string): FileMetadata[] {
    const removeIndex = files.findIndex(item => item.fileKey === fileKey);
    if (removeIndex !== -1) {
      files.splice(removeIndex, 1);
    }
    return files;
  }

  private updateFullFileMetadata(fileKey: string): void {
    const allFilesData = this.docMgmtService.fullFileMetadata.value;
    const updatedAllFileData = this.removeFileByFileKey(allFilesData, fileKey);
    this.docMgmtService.fullFileMetadata.next(updatedAllFileData);
  }

  private getTableData(): FileMetadata[] {
    return this.tableData;
  }

  private setTableData(value: FileMetadata[]) {
    this.tableData = value;
  }

  private setIsDeleteInProgress(value: boolean) {
    this.isDeleteInProgress = value;
  }
}
